<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="inbox"/> {{ $t('chatHistory') }}</h1>
        <p>{{ $t('chatHistoryDesc') }}</p>
      </div>
    </div>

    <div class="row">
      <!-- Tabs hướng tin nhắn -->
      <div class="col-12">
        <b-tabs v-model="dirTabIndex" @input="onDirTab">
          <b-tab :title="$t('all')"></b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #1976d2"></span> {{ $t('sendFromCustomer') }}
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #28a745"></span> {{ $t('sendFromCallio') }}
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <client-select class="mr-1 mb-1" :model="filterFormModel" attribute="client" :isFilter="true"
                           :place-holder="$t('selectClient')"></client-select>
            <time-range-picker :model="filterFormModel" :label="$t('time')" class="mr-1 mb-1"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
            <chat-export class="ml-auto mb-1"
                         v-if="['owner','admin','tech','operationsManager','account'].includes($store.state.user.role)"
                         :filter="filterFormModel" :total-count="itemCount"></chat-export>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount > 0">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length > 0">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card"
               :style="{ borderLeftColor: item.fromContact ? '#1976d2' : '#28a745' }">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm:ss') }}</div>
              <div class="log-meta">{{ item.createTime | moment('YYYY') }}</div>
            </div>

            <div class="log-card__type">
              <span class="log-type-pill"
                    :style="{ background: item.fromContact ? '#1976d2' : '#28a745' }">
                <font-awesome-icon :icon="item.fromContact ? 'sign-in-alt' : 'sign-out-alt'" class="mr-1"/>
                {{ item.fromContact ? $t('sendFromCustomer') : $t('sendFromCallio') }}
              </span>
              <div class="log-meta mt-1" v-if="item.socialNetworkAccount">{{ item.socialNetworkAccount.name }}</div>
            </div>

            <div class="log-card__main">
              <div class="log-row-1">
                <font-awesome-icon icon="user" class="text-muted mr-1"/>
                <strong>{{ item.customer.name }}</strong>
                <span class="log-meta ml-3" v-if="item.client">
                  <font-awesome-icon icon="building" class="mr-1"/>{{ item.client.domain }}
                </span>
              </div>
              <div class="log-row-2 log-content">
                <span v-if="item.content">{{ item.content }}</span>
                <a v-else-if="item.attachments && item.attachments.length" :href="item.attachments[0].url"
                   target="_blank" rel="noopener noreferrer" class="text-info">
                  <font-awesome-icon icon="paperclip" class="mr-1"/> {{ $t('attachment') }}
                </a>
                <span v-else class="text-muted">—</span>
              </div>
              <div class="log-row-3" v-if="item.user">
                <font-awesome-icon icon="headset" class="text-muted mr-1"/>
                <span class="log-meta">{{ item.user.name }} ({{ item.user.ext }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-4" v-if="items.length === 0 && !loading">
        <font-awesome-icon icon="inbox" size="3x" class="mb-2 d-block"/>
        {{ $t('notFound') }}
      </div>

      <div class="col-12" v-if="itemCount > filterFormModel.pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import List from "@/mixins/List";
import TimeRangePicker from "@/components/TimeRangePicker.vue";
import ClientSelect from "@/components/ClientSelect.vue";
import ChatExport from "@/views/message/export/ChatExport.vue";

export default {
  components: { ChatExport, ClientSelect, TimeRangePicker },
  mixins: [List],
  data() {
    return {
      service: '/customer-log',
      autoLoad: true,
      dirTabIndex: 0,
      defaultFilterFormModel: {
        client: this.clientId || null,
        fromContact: null,
      }
    }
  },
  methods: {
    onDirTab(idx) {
      this.filterFormModel.fromContact = idx === 0 ? null : (idx === 1);
      this.filterFormModel.page = 1;
      this.load();
    }
  },
  i18n: {
    messages: {
      en: {
        chatHistory: 'Chat history',
        chatHistoryDesc: 'Chat messages between customers and agents.',
        all: 'All',
        selectClient: 'Select client',
        sendFromCustomer: 'From customer',
        sendFromCallio: 'From Callio',
        attachment: 'Attachment',
        notFound: 'No records found',
      },
      vi: {
        chatHistory: 'Lịch sử chat',
        chatHistoryDesc: 'Tin nhắn giữa khách hàng và nhân viên.',
        all: 'Tất cả',
        selectClient: 'Chọn khách hàng',
        sendFromCustomer: 'Khách gửi',
        sendFromCallio: 'NV gửi',
        attachment: 'Đính kèm',
        notFound: 'Không tìm thấy bản ghi nào',
      }
    }
  }
}
</script>

<style scoped>
.log-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.log-list { display: flex; flex-direction: column; gap: 8px; }
.log-card {
  display: flex; align-items: flex-start; background: #fff;
  border: 1px solid #e8eaed; border-left: 4px solid #888; border-radius: 6px;
  padding: 12px 16px; gap: 16px; transition: all 0.15s;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.log-card__time { min-width: 110px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }
.log-card__type { min-width: 150px; flex-shrink: 0; }
.log-type-pill {
  display: inline-block; padding: 3px 10px; border-radius: 12px;
  color: #fff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px; white-space: nowrap;
}
.log-card__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.log-row-1 { font-size: 14px; color: #222; }
.log-row-2 { font-size: 13px; color: #444; word-break: break-word; }
.log-content { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.log-row-3 { font-size: 12px; }
</style>
